const CLAMP_CONTENT_SELECTOR = '.clamp-content';
const TOGGLE_BUTTON_SELECTOR = '.toggleButton';
const CONTAINER_SELECTOR = '.container, .container-fluid';
const CLAMP_TEXT_CLASS = 'clamp-text';
const READ_MORE_TEXT = 'Read More';
const READ_LESS_TEXT = 'Read Less';

const setBoxOrientVertical = (el: HTMLElement): void => {
  el.style.setProperty('-webkit-box-orient', 'vertical', 'important');
};

const removeBoxOrient = (el: HTMLElement): void => {
  el.style.removeProperty('-webkit-box-orient');
};

const initializeClampContent = (root: ParentNode = document): void => {
  const clampElements = root.querySelectorAll<HTMLElement>(CLAMP_CONTENT_SELECTOR);
  clampElements.forEach((element) => {
    setBoxOrientVertical(element);
  });
};

const getButtonWrapper = (contentBlock: HTMLElement): HTMLElement | null => {
  const container = contentBlock.closest(CONTAINER_SELECTOR) as HTMLElement | null;
  if (!container) return null;
  const button = container.querySelector<HTMLElement>(TOGGLE_BUTTON_SELECTOR);
  return button?.parentElement ?? null;
};

const isOverflowing = (contentBlock: HTMLElement): boolean => {
  const wasClamped = contentBlock.classList.contains(CLAMP_TEXT_CLASS);

  if (!wasClamped) {
    contentBlock.classList.add(CLAMP_TEXT_CLASS);
    setBoxOrientVertical(contentBlock);
  }

  const overflows = contentBlock.scrollHeight > contentBlock.clientHeight;

  if (!wasClamped) {
    contentBlock.classList.remove(CLAMP_TEXT_CLASS);
    removeBoxOrient(contentBlock);
  }

  return overflows;
};

const updateButtonVisibility = (root: ParentNode = document): void => {
  const clampElements = root.querySelectorAll<HTMLElement>(CLAMP_CONTENT_SELECTOR);
  clampElements.forEach((contentBlock) => {
    const buttonWrapper = getButtonWrapper(contentBlock);
    if (!buttonWrapper) return;

    buttonWrapper.classList.toggle('d-none', !isOverflowing(contentBlock));
  });
};

let resizeTimeout: ReturnType<typeof setTimeout>;

const handleResize = (): void => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(updateButtonVisibility, 150);
};

const handleToggleClick = (button: HTMLElement): void => {
  const container = button.closest(CONTAINER_SELECTOR) as HTMLElement | null;
  if (!container) {
    console.warn('Container not found for toggle button:', button);
    return;
  }

  const contentBlock = container.querySelector<HTMLElement>(CLAMP_CONTENT_SELECTOR);
  if (!contentBlock) {
    console.warn('Content block not found in container:', container);
    return;
  }

  const isClamped = contentBlock.classList.contains(CLAMP_TEXT_CLASS);

  if (isClamped) {
    expandContent(contentBlock, button);
  } else {
    collapseContent(contentBlock, button);
  }
};

const expandContent = (contentBlock: HTMLElement, button: HTMLElement): void => {
  contentBlock.classList.remove(CLAMP_TEXT_CLASS);
  button.textContent = READ_LESS_TEXT;
  removeBoxOrient(contentBlock);
};

const collapseContent = (contentBlock: HTMLElement, button: HTMLElement): void => {
  contentBlock.classList.add(CLAMP_TEXT_CLASS);
  button.textContent = READ_MORE_TEXT;
  setBoxOrientVertical(contentBlock);
};

const setupToggleButtons = (): void => {
  const toggleButtons = document.querySelectorAll<HTMLElement>(TOGGLE_BUTTON_SELECTOR);

  toggleButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      handleToggleClick(button);
    });
  });
};

const init = (): void => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeClampContent();
      setupToggleButtons();
      updateButtonVisibility();
      window.addEventListener('resize', handleResize);
    });
  } else {
    initializeClampContent();
    setupToggleButtons();
    updateButtonVisibility();
    window.addEventListener('resize', handleResize);
  }
};

export { init, initializeClampContent, updateButtonVisibility, handleToggleClick };
